/**
 * The currencies an invoice can be denominated in.
 *
 * A short list, unlike countries, and deliberately so. The argument for all 250
 * countries is that somebody's actual country must be enterable; the argument
 * does not carry over, because a currency is a decision about how we bill rather
 * than a fact about the customer. Adding one means the invoicing code has to
 * handle it — rounding, minor units, the exchange rate it is reported in — so
 * the list is what we can actually invoice, and it grows when that does.
 *
 * ISO 4217 codes, which is what the `currency` column holds.
 */

export interface Currency {
  code: string;
  name: string;
}

export const CURRENCIES: readonly Currency[] = [
  { code: 'EUR', name: 'Euro' },
  { code: 'USD', name: 'US dollar' },
  { code: 'GBP', name: 'Pound sterling' },
  { code: 'CHF', name: 'Swiss franc' },
  { code: 'SEK', name: 'Swedish krona' },
  { code: 'NOK', name: 'Norwegian krone' },
  { code: 'DKK', name: 'Danish krone' },
  { code: 'PLN', name: 'Polish złoty' },
  { code: 'CZK', name: 'Czech koruna' },
  { code: 'CAD', name: 'Canadian dollar' },
  { code: 'MXN', name: 'Mexican peso' },
  { code: 'BRL', name: 'Brazilian real' },
  { code: 'JPY', name: 'Japanese yen' },
  { code: 'CNY', name: 'Chinese yuan' },
  { code: 'KRW', name: 'South Korean won' },
  { code: 'INR', name: 'Indian rupee' },
  { code: 'AUD', name: 'Australian dollar' },
  { code: 'NZD', name: 'New Zealand dollar' },
  { code: 'TRY', name: 'Turkish lira' },
  { code: 'ZAR', name: 'South African rand' },
];

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

/**
 * "EUR — Euro" for display, or the raw code if it is not one we invoice in.
 *
 * Falls back to the value rather than to blank, so a row holding something
 * unexpected shows what it holds instead of looking unset.
 */
export function currencyLabel(code: string | null | undefined): string {
  if (!code) return '';
  const c = BY_CODE.get(code.trim().toUpperCase());
  return c ? `${c.code} — ${c.name}` : code;
}
